/* ===========================================================================
 * "GOOD AFTERNOON, AHMAD" — ONCE, IN THE HEADER, AND NOWHERE ELSE.
 *
 * It used to be an h1 on the worker's home screen, over the check-in map, with
 * the signed-in NAME repeated in the header bar forty pixels above it. Two
 * lines saying who you are, on the one screen whose whole job is a single
 * button — and on a phone the greeting and the date pushed the punch control
 * most of the way down the first screenful.
 *
 * So the greeting IS the identity now: the header says "Good afternoon, Ahmad"
 * where it used to say "Ahmad", the home screen says neither, and there is one
 * source for both facts.
 *
 * THE FIRST WORD, NOT THE FIRST NAME. A Gulf payroll register holds names like
 * "ABDALLAH MOHAMMAD Ahmad Hussein", where the given name spans three of the
 * four words and no field says which. Taking the first word is the only rule
 * that is right for everybody: it is a name the person answers to, it never
 * truncates mid-word, and it cannot accidentally address somebody by their
 * father's name because it guessed at a boundary that is not recorded.
 *
 * THE HOUR IS THE COMPANY'S, NOT THE BROWSER'S (docs/time-and-dates.md). A
 * supervisor checking the console from another country is greeted by the site's
 * afternoon, because that is the working day this product is about.
 *
 * The thresholds are `pickGreeting`'s, the server helper the dashboard used —
 * so moving the greeting did not move where the line between morning and
 * afternoon is.
 */

export type GreetingBucket = 'morning' | 'afternoon' | 'evening';

/** The bucket for an hour of the day. Mirrors `pickGreeting` in the API. */
export function greetingBucket(hour: number): GreetingBucket {
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

/**
 * The bucket for `now` read in the company's timezone.
 *
 * `Intl` rather than the browser's own hour, and `hour12: false` so a locale
 * that formats midnight as 12 cannot land the reader in the afternoon. `% 24`
 * is the documented ICU guard `lib/dates.ts` carries for the same reason.
 */
export function greetingBucketIn(timeZone: string, now: Date = new Date()): GreetingBucket {
  const hour = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    hour: '2-digit',
  }).formatToParts(now).find((part) => part.type === 'hour')?.value;
  return greetingBucket(Number(hour ?? '0') % 24);
}

/**
 * The word to greet somebody by: the first word of their display name, or the
 * local part of their email when there is no name on the record.
 *
 * `null` when there is neither, so the caller shows what it shows for a session
 * with no identity rather than greeting an empty string.
 */
export function greetingName(displayName?: string | null, email?: string | null): string | null {
  const first = displayName?.trim().split(/\s+/)[0];
  if (first) return first;
  const local = email?.trim().split('@')[0];
  return local && local !== '' ? local : null;
}
